"use client";

import { type Icon, IconActivity, IconBrain, IconCurrencyDollar, IconStack2 } from "@tabler/icons-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { formatCostUsd, useUsageSummary } from "@/lib/usage/usage.hooks";

const compact = new Intl.NumberFormat("en-US", {
  notation: "compact",
  maximumFractionDigits: 1,
});

const PERIOD_30D = { preset: "last_30d" } as const;

export const DashboardUsageStats = () => {
  const { data, isLoading } = useUsageSummary(PERIOD_30D);
  const summary = data?.usage_summary;

  const cards: { label: string; value: string; Icon: Icon }[] = [
    { label: "Total requests", value: isLoading ? "—" : (summary?.requests ?? "0"), Icon: IconActivity },
    {
      label: "Total tokens",
      value: isLoading ? "—" : compact.format(Number(summary?.total_tokens ?? 0)),
      Icon: IconStack2,
    },
    {
      label: "Estimated spend",
      value: isLoading ? "—" : formatCostUsd(summary?.estimated_cost_cents ?? 0),
      Icon: IconCurrencyDollar,
    },
    {
      label: "Active models",
      value: isLoading ? "—" : String(summary?.distinct_models ?? 0),
      Icon: IconBrain,
    },
  ];

  return (
    <div className="grid gap-4 px-4 sm:grid-cols-2 lg:grid-cols-4 lg:px-6">
      {cards.map(({ label, value, Icon }) => (
        <Card key={label}>
          <CardHeader className="pb-2">
            <div className="mb-2 flex size-9 items-center justify-center rounded-md border bg-sidebar">
              <Icon className="size-5 text-muted-foreground" aria-hidden />
            </div>
            <CardTitle className="text-sm font-medium text-muted-foreground">{label}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-semibold tabular-nums">{value}</p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};
